import { useCallback, useEffect, useState } from 'react';
import { isTrustedKizenMessageOrigin } from 'utility/messageOrigins';

// Plugin iframes are routed through the frame proxy (plugin-assets.kizen.{dev,com})
// by the engine's `buildIframeURLWithProxy`. The proxy renders the real plugin
// content inside a nested sandboxed iframe and posts a message to the parent
// window once that inner frame has loaded. The outer iframe's native `onLoad`
// only signals that the proxy *shell* loaded — which happens long before the
// plugin content is ready — so we rely on this message to clear loading state.
//
// Message shape (see plugin-frame-proxy/index.html):
//   { plugin_api_name, source_url, event: 'loaded' | 'error' | 'message', _kizen_proxy_nonce }
type FrameProxyMessage = {
  plugin_api_name?: string;
  source_url?: string;
  event?: 'loaded' | 'error' | 'message';
  error?: string;
};

// Fallback ceiling: if the proxy never reports back (e.g. the proxy shell
// itself fails to load, so no message — including its own error — ever
// arrives), stop blocking after this long so the loader can't spin forever.
const FRAME_PROXY_LOAD_TIMEOUT_MS = 30_000;

/**
 * Tracks whether the proxied plugin frame for `pluginApiName` has finished
 * loading. Returns `true` once the proxy reports the content is loaded (or that
 * loading errored — in which case the proxy renders its own error overlay, so
 * we stop blocking on our side), or once `timeoutMs` elapses as a fallback.
 * Resets whenever the frame identity changes.
 *
 * Pass `undefined` when the current view isn't a proxied iframe — the hook
 * stays inert (no listener, no timeout) and returns `true`.
 */
export const usePluginFrameProxyLoaded = (
  pluginApiName?: string,
  timeoutMs = FRAME_PROXY_LOAD_TIMEOUT_MS
) => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(false);
  }, [pluginApiName]);

  const handleMessage = useCallback(
    (e: MessageEvent) => {
      // The proxy posts from plugin-assets.kizen.{dev,com}; ignore messages
      // from anywhere outside kizen domains (e.g. the plugin's own content
      // spoofing a "loaded" event from its nested iframe).
      if (!isTrustedKizenMessageOrigin(e.origin)) {
        return;
      }
      const message = e.data as FrameProxyMessage | undefined;
      if (!message || typeof message !== 'object') {
        return;
      }
      // Only act on the frame we own — the proxy stamps each message with the
      // `name` we set on the iframe element.
      if (message.plugin_api_name !== pluginApiName) {
        return;
      }
      if (message.event === 'loaded' || message.event === 'error') {
        setLoaded(true);
      }
    },
    [pluginApiName]
  );

  useEffect(() => {
    if (!pluginApiName) {
      return;
    }
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [pluginApiName, handleMessage]);

  useEffect(() => {
    if (!pluginApiName || loaded) {
      return;
    }
    const id = window.setTimeout(() => setLoaded(true), timeoutMs);
    return () => {
      window.clearTimeout(id);
    };
  }, [loaded, timeoutMs, pluginApiName]);

  return pluginApiName ? loaded : true;
};
